import BackButton from '@/components/BackButton';
import CtaButton from '@/components/CtaButton';
import CtaText from '@/components/CtaText';
import ErrorMessage from '@/components/ErrorMessage';
import CheckboxInput from '@/components/Forms/CheckboxInput';
import PasswordInput from '@/components/Forms/PasswordInput';
import TextInput from '@/components/Forms/TextInput';
import LoadingSpinner from '@/components/Icons/LoadingSpinner';
import RightCheveron from '@/components/Icons/RightCheveron';
import PageLayout from '@/components/PageLayout';
import { NavBarLightTheme } from '@/components/PageLayout/PageNavBar';
import TwelveColGrid from '@/components/TwelveColGrid';
import useHandleErrorLogic from '@/hooks/useHandleErrorLogic';
import { SignupFormValues, User } from '@/lib/interfaces';
import {
  CLIENT_PRIVACY_POLICY_URL,
  CLIENT_SIGNUP_URL,
  CLIENT_TERMS_AND_CONDITIONS_URL,
} from '@/utils/constants';
import { SignUpSchema } from '@/utils/forms';
import { registerUser } from '@/utils/http/userAuthRequests';
import { routeUserAfterLogin } from '@/utils/v2HelperMethods';
import { verifyUser } from '@/utils/v2HttpRequests';
import axios from 'axios';
import { Field, Form, Formik } from 'formik';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const { redirect = null } = query;
  return { props: { redirect } };
};

export default function SignUpPage({
  redirect,
}: Readonly<{ redirect: string | null }>) {
  const router = useRouter();
  const [err, setErr] = useHandleErrorLogic();

  const initialValues: SignupFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirmation: '',
    phoneNumber: '',
    city: '',
    tcAgree: false,
    updatesAgree: false,
    smsOptedIn: false,
  };

  return (
    <PageLayout>
      <NavBarLightTheme sticky={false} />
      <TwelveColGrid my="mt-3.2 lgt:mt-6.4">
        <ErrorMessage error={err} />
        <div className="flex justify-center col-span-12">
          <BackButton href={CLIENT_SIGNUP_URL} />
        </div>

        <h3 className="col-span-12 mt-1.6 font-medium text-center text-black-100 copy-l">
          PERSONAL INFO
        </h3>

        <h3 className="col-span-12 text-center text-body text-black-300">
          We need this to get to know you better
        </h3>

        <div className="col-span-12 lgt:col-start-5 lgt:col-span-4 mt-3.2 lgt:mt-4.8">
          <Formik
            validationSchema={SignUpSchema}
            initialValues={initialValues}
            onSubmit={async (values, actions) => {
              try {
                await registerUser(values);
                const user: User = await verifyUser();
                actions.setSubmitting(false);
                console.log(user);

                await routeUserAfterLogin({ user, router, redirect });
              } catch (error) {
                if (axios.isAxiosError(error)) {
                  const errorMessage = error.response?.data.error.message;
                  setErr(errorMessage);
                } else {
                  setErr(false);
                }
                actions.setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, isValid }) => (
              <Form className="flex flex-col justify-between">
                <div>
                  <Field
                    type="text"
                    name="firstName"
                    placeholder=" "
                    labelCopy="First name"
                    component={TextInput}
                  />
                  <Field
                    type="text"
                    name="lastName"
                    placeholder=" "
                    labelCopy="Last name"
                    component={TextInput}
                  />
                  <Field
                    type="text"
                    name="city"
                    placeholder=" "
                    labelCopy="Current city"
                    component={TextInput}
                  />
                  <Field
                    type="email"
                    name="email"
                    placeholder=" "
                    labelCopy="Email"
                    component={TextInput}
                  />
                  <Field
                    name="password"
                    placeholder=" "
                    labelCopy="Password"
                    component={PasswordInput}
                  />
                  <Field
                    name="passwordConfirmation"
                    placeholder=" "
                    labelCopy="Confirm password"
                    component={PasswordInput}
                  />
                  <Field
                    type="tel"
                    name="phoneNumber"
                    placeholder=" "
                    labelCopy="Phone"
                    component={TextInput}
                  />
                  <div className="mb-1.6 h-1"></div>
                  <div className="mb-2.4">
                    <Field
                      checkboxCopy="I would like to recieve updates from OLEA"
                      type="checkbox"
                      name="updatesAgree"
                      component={CheckboxInput}
                    />
                  </div>
                  <div className="mb-2.4">
                    <Field
                      checkboxCopy={
                        <div>
                          I agree to the
                          <Link
                            href={CLIENT_PRIVACY_POLICY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium mx-0.3"
                          >
                            {' '}
                            Privacy Policy
                          </Link>
                          and
                          <Link
                            href={CLIENT_TERMS_AND_CONDITIONS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium mx-0.3"
                          >
                            Terms &amp; Conditions
                          </Link>
                        </div>
                      }
                      type="checkbox"
                      name="tcAgree"
                      component={CheckboxInput}
                    />
                  </div>
                </div>
                <div className="flex justify-center w-full mx-auto mt-3.2 mb-6.4">
                  <CtaButton
                    height="h-4.8"
                    width="w-34.4 lgt:w-36.0"
                    type="submit"
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? (
                      <>
                        <CtaText>PROCESSING</CtaText>
                        <LoadingSpinner />
                      </>
                    ) : (
                      <>
                        <CtaText>SUBMIT</CtaText>
                        <RightCheveron />
                      </>
                    )}
                  </CtaButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </TwelveColGrid>
    </PageLayout>
  );
}
